import { Directive, Injectable, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDateRangePicker, MatDateSelectionModel, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Range selection model that never reports complete. MatDatepickerContent._finishSelection
 * closes the popup when the model completes — reporting incomplete keeps the popup open so the
 * Cancel/Select buttons own the close.
 */
@Injectable()
export class PersistentRangeSelectionModel extends MatRangeDateSelectionModel<Date> {
  constructor(adapter: DateAdapter<Date>) {
    super(adapter);
  }

  isComplete(): boolean {
    return false;
  }
}

/**
 * Explicit Cancel/Select actions for a mat-date-range-picker (Material 10 has no built-in
 * picker actions — they arrived in v12), with a deferred commit: day clicks land on the
 * directive's pending pair only, so nothing reaches the consumer's controls — and its API
 * calls — until Select. Attach on the picker element and bind the range inputs to the
 * pending pair through the exportAs:
 *
 *   <mat-date-range-picker appApprovedPickerActions #actions="appApprovedPickerActions"
 *     [start]="fromControl" [end]="toControl">
 *     <input matStartDate [formControl]="actions.pendingStart">
 *     <input matEndDate [formControl]="actions.pendingEnd">
 *
 * - On open: syncs the pending pair to the committed range and appends the action row to the
 *   panel content (the panel has no projection slot, so the row is built imperatively — see
 *   the .fh-picker-actions styles in purchase-orders.component.scss).
 * - Select: disabled until both pending bounds have a date; on click it commits the pending
 *   range to the consumer's controls — the end bound emits so the consumer's valueChanges
 *   wiring revalidates/refetches exactly once — and closes.
 * - Cancel: clears the committed range (the consumer's search drops the filter and
 *   refetches once) and closes. Backdrop / escape just close — the committed range stays.
 *
 * Also provides PersistentRangeSelectionModel at the picker element: the popup content's
 * MAT_RANGE_DATE_SELECTION_MODEL_PROVIDER factory reuses a parent model when one exists
 * (parent || new), so this isComplete() override reaches the content's auto-close check.
 */
@Directive({
  selector: '[appApprovedPickerActions]',
  exportAs: 'appApprovedPickerActions',
  providers: [
    { provide: MatDateSelectionModel, useClass: PersistentRangeSelectionModel },
  ],
})
export class ApprovedPickerActionsDirective implements OnInit, OnDestroy {
  /* istanbul ignore next -- decorator metadata */
  @Input() start: FormControl;
  /* istanbul ignore next -- decorator metadata */
  @Input() end: FormControl;

  /** Shadow pair the calendar writes to — the committed range only changes through Select. */
  readonly pendingStart = new FormControl(null);
  readonly pendingEnd = new FormControl(null);

  private readonly _destroy$ = new Subject<void>();
  private _actionsRow: HTMLDivElement | null = null;
  private _selectButton: HTMLButtonElement | null = null;
  private _confirmed = false;

  constructor(private readonly _picker: MatDateRangePicker<Date>) {}

  ngOnInit(): void {
    this._syncPending();
    // Material writes the pending pair as days are clicked — keep Select's disabled state
    // in lockstep (a third click restarts the range and re-disables it).
    merge(this.pendingStart.valueChanges, this.pendingEnd.valueChanges)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._syncSelectDisabled());
    this._picker.openedStream.pipe(takeUntil(this._destroy$)).subscribe(() => this._onOpened());
    this._picker.closedStream.pipe(takeUntil(this._destroy$)).subscribe(() => this._onClosed());
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._removeActions();
  }

  private _onOpened(): void {
    this._syncPending();
    // The panel has no projection slot — build the action row and append it to the content.
    const panel = document.querySelector('.mat-datepicker-popup mat-datepicker-content');
    if (!panel) {
      return;
    }
    const row = document.createElement('div');
    row.className = 'fh-picker-actions';
    row.appendChild(this._actionButton('Cancel', 'fh-picker-actions__cancel', () => {
      this._clearCommitted();
      this._picker.close();
    }));
    const select = this._actionButton('Select', 'fh-picker-actions__select', () => {
      this._commitPending();
      this._confirmed = true;
      this._picker.close();
    });
    row.appendChild(select);
    panel.appendChild(row);
    this._actionsRow = row;
    this._selectButton = select;
    this._syncSelectDisabled();
  }

  private _onClosed(): void {
    this._removeActions();
    if (!this._confirmed) {
      // Discard the picks so the box reverts to the committed range.
      this._syncPending();
    }
    this._confirmed = false;
  }

  // The only path that writes the consumer's controls: the start bound is set silently and
  // the end bound emits, so the consumer's valueChanges wiring fires exactly once with both
  // values already in place.
  private _commitPending(): void {
    if (!this.start || !this.end) {
      return;
    }
    this.start.setValue(this.pendingStart.value, { emitEvent: false });
    this.end.setValue(this.pendingEnd.value);
  }

  // Cancel clears the committed range — the consumer's search drops the approved-at filter
  // and refetches exactly once (same silent-start/emitting-end pattern). No-op when the
  // filter is already empty, so an open+cancel on a clean filter costs no refetch.
  private _clearCommitted(): void {
    if (!this.start || !this.end) {
      return;
    }
    if (this.start.value == null && this.end.value == null) {
      return;
    }
    this.start.setValue(null, { emitEvent: false });
    this.end.setValue(null);
  }

  private _syncPending(): void {
    this.pendingStart.setValue(this.start?.value ?? null, { emitEvent: false });
    this.pendingEnd.setValue(this.end?.value ?? null, { emitEvent: false });
  }

  private _syncSelectDisabled(): void {
    if (!this._selectButton) {
      return;
    }
    this._selectButton.disabled = !(this.pendingStart.value && this.pendingEnd.value);
  }

  private _actionButton(label: string, className: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  private _removeActions(): void {
    this._actionsRow?.remove();
    this._actionsRow = null;
    this._selectButton = null;
  }
}
